import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { buildReviewSchema, buildStepSchemas } from "../validation.schema";
import { levelledLanguageKeys } from "@/utils/languageRequirements";
import { buildConfirmPayload } from "../utils/buildConfirmPayload";
import toast from "react-hot-toast";

export const defaultValues = {
  personal_information: {
    name: "",
    email: "",
    alternate_email: "",
    phone: "",
    location: "",
    linkedin: "",
    github: "",
    portfolio: "",
    summary: "",
  },
  skills: {
    list: [],
    languages: [],
  },
  experience: [
    {
      jobTitle: "",
      company: "",
      startDate: "",
      endDate: "",
      location: "",
      description: "",
    },
  ],
  education: [
    {
      degree: "",
      institution: "",
      fieldOfStudy: "",
      startDate: "",
      endDate: "",
      grade: "",
    },
  ],
  additionalSections: [
    {
      section_title: "",
      items: [""],
    },
  ],
  social: {
    instagram_url: "",
    facebook_url: "",
    twitter_url: "",
  },
  compensation: {
    current_salary: "",
    expected_salary: "",
    notice_period: "",
    total_experience: "",
  },
};

/**
 * The review form: schema built from the job's mandatory fields, step state, and
 * the submit transform.
 *
 * The starting values arrive as `defaultValues`, which is what all three callers
 * (confirm-details, apply, bulk review) have always passed. The parameter used
 * to be named `initialData`, so every one of those calls was silently ignored
 * and the form fell back to the module constant below — right by accident, and a
 * trap for the first caller to pass real values.
 */
export const useReviewForm = ({
  defaultValues: initialValues,
  onSubmit,
  mandatoryFields = [],
  languagesShape = "rows",
  // The job's own required language ROWS — [{ language, proficiency }], not
  // names. The levels are the point: the schema refuses a blank level only on
  // the languages the job ITSELF levelled, so a caller handing over names with
  // the levels stripped off cannot say which those are, and every job language
  // ends up demanding a level the recruiter never asked for.
  requiredLanguages = [],
}) => {
  const [step, setStep] = useState(0);

  // The whole of what the schema reads out of `requiredLanguages`, and so the
  // only dep that tracks it. `requiredLanguages.join(",")` is "[object Object]"
  // for every row alike, which made the levels invisible to the memo: two jobs
  // differing only in whether they named one shared the first one's schema.
  const levelledKey = useMemo(
    () => [...levelledLanguageKeys(requiredLanguages)].sort().join(","),
    [requiredLanguages],
  );

  // DYNAMIC SCHEMA based on mandatory fields. The candidate wizards (the only
  // consumers) render languages as paired dropdowns, so the rows shape is the
  // default here — admin flows build their own schemas and opt in explicitly.
  // `languagesShape` is a dep, not just an argument: it decides which of the
  // two languages schemas gets built, so leaving it out of the array serves
  // the first caller's shape for the life of the form.
  const stableSchema = useMemo(
    () => buildReviewSchema(mandatoryFields, true, { languagesShape, requiredLanguages }),
    [mandatoryFields.join(","), languagesShape, levelledKey],
  );
  const dynamicStepSchemas = useMemo(
    () => buildStepSchemas(mandatoryFields, true, { languagesShape, requiredLanguages }),
    [mandatoryFields.join(","), languagesShape, levelledKey],
  );

  // FORM INIT
  const methods = useForm({
    resolver: zodResolver(stableSchema),
    defaultValues: initialValues || defaultValues,
    mode: "onChange",
    shouldUnregister: false,
    shouldFocusError: true,
  });

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitting, errors },
  } = methods;

  // Compute which step indices have at least one validation error
  const stepsWithErrors = dynamicStepSchemas.map((schema) => Object.keys(schema.shape).some((field) => !!errors[field]));

  // STEP NAVIGATION
  const nextStep = async () => {
  const fields = Object.keys(dynamicStepSchemas[step].shape);
  const isValid = await trigger(fields, { shouldFocus: false });
  if (!isValid) return false;
  setStep((prev) => prev + 1);
  return true;
};

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const goToStep = async (targetStep) => {
    if (targetStep === step) return;
    if (targetStep > step) {
      const fields = Object.keys(dynamicStepSchemas[step].shape);
      const isValid = await trigger(fields);
      if (!isValid) return;
    }
    setStep(targetStep);
  };

  const submitForm = handleSubmit(async (data) => {
    try {
      const parsed = stableSchema.parse(data);
      const transformed = buildConfirmPayload(parsed);

      await onSubmit?.(transformed);
    } catch (err) {
      console.error("Submit Error:", err);
      toast.error(err.message || "Failed to submit. Please try again.");
    }
  });

  const isFirstStep = step === 0;
  const isLastStep = step === dynamicStepSchemas.length - 1;

  return {
    methods,
    step,
    setStep,
    nextStep,
    prevStep,
    goToStep,
    submitForm,
    isSubmitting,
    isFirstStep,
    isLastStep,
    stepsWithErrors,
  };
};